import { useEffect, useCallback, useRef } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import {
  CartItem,
  CartItemDesign,
  CartItemLogo,
} from "../presentation/screens/order-create/models/CartItem";
import { BaseLogo } from "../core/models/entities/BaseLogo";
import { Design } from "../core/models/entities/Design";
import { useProfileContext } from "../contexts/ProfileContext";
import { useApiClient } from "../data/clients/ApiClient";
import { actAsCacheKey } from "../contexts/actAsStore";
import { mapToLogo } from "../data/models/mappers/mapLogo";
import { mapToDesign } from "../data/models/mappers/mapDesign";
import {
  cartDesignPendingChanged,
  cartLogoPendingChanged,
} from "../utils/functions/productPending";
import { patchLogoRowsFromDetail } from "../data/repositories/logoDetailCache";
import { hasAdjustedPrices } from "../data/repositories/designCachePatch";
import { enviroment } from "../enviroment";
import { priceFieldsDiffer } from "../utils/functions/cartPriceCheck";
import { collectCartLogoIds } from "../presentation/screens/order-confirmation/storageVerify";
import { landCartPriceCheck } from "./useCartPriceCheck";

// Cart price check (flag cartPriceCheckEnabled): a design line's total
// prices are compared like a logo line's price rows, so the ONE GetDesign
// the check asks for on "updated" lands the fresh total in the line. A
// fresh total without a single ADJUSTED row - totalPrices is [NotMapped]
// in the API and only AdjustPrice fills it, so an answer where it did not
// run (a transient price failure, a pending product) carries it empty or
// as persisted-only rows - carries no information: the line keeps the
// price it has, exactly as the design cache patch keeps its cached
// container (designCachePatch, PRICES); never "stale", never the note's
// fallback. Always false with the flag off (the historical rule: storage /
// pending only).
function designTotalPricesChanged(
  cartDesign: CartItemDesign,
  freshDesign: Design
): boolean {
  if (!enviroment.cartPriceCheckEnabled) return false;
  if (!hasAdjustedPrices(freshDesign.product?.totalPrices)) return false;
  return priceFieldsDiffer(
    cartDesign.design.product?.totalPrices,
    freshDesign.product?.totalPrices
  );
}

interface UseCartSyncProps {
  items: CartItem[];
  updateItem: (item: CartItem) => Promise<CartItem>;
}

interface UseCartSyncReturn {
  isRefreshing: boolean;
  hasStaleItems: boolean;
  refreshCart: () => void;
}

export function useCartSync({
  items,
  updateItem,
}: UseCartSyncProps): UseCartSyncReturn {
  const apiClient = useApiClient();
  const { profile } = useProfileContext();
  const queryClient = useQueryClient();

  // Extract logo items from cart
  const logoItems = items.filter(
    (item): item is CartItemLogo => item.type === "Logo"
  );

  // Extract design items from cart
  const designItems = items.filter(
    (item): item is CartItemDesign => item.type === "Design"
  );

  // Create queries for all logos in the cart.
  // Key shape MUST match LogoRepository.useLogo (["logos", actAs, id]) so
  // cart sync and the detail popup share one cache entry; getLogo varies with
  // the act-as scope since #603 (prices in the acted-as vendor's currency),
  // hence the key segment. Same shared helper as the repository's own keys.
  const logoActAsKey = actAsCacheKey();
  const logoQueries = useQueries({
    queries: logoItems.map((logoItem) => ({
      queryKey: ["logos", logoActAsKey, logoItem.logoId],
      queryFn: () =>
        apiClient
          .getLogo(
            profile?.userOrganizationInformations.organizationId || 0,
            logoItem.logoId
          )
          .then(mapToLogo),
      enabled:
        !!profile?.userOrganizationInformations.organizationId &&
        logoItem.logoId > 0,
      staleTime: 1 * 30 * 1000,
    })),
  });

  // Create queries for all designs in the cart.
  // Key shape MUST match DesignRepository.useDesign (["designs", actAs, id])
  // so cart sync and the detail modal share one cache entry; getDesign now
  // varies with the act-as scope (actAsOrgId param), hence the key segment.
  // Same shared helper as the logo key below (actAsCacheKey == getActAsOrgId() ?? 0).
  const designActAsKey = actAsCacheKey();
  const designQueries = useQueries({
    queries: designItems.map((designItem) => ({
      queryKey: ["designs", designActAsKey, designItem.design.id],
      queryFn: () =>
        apiClient
          .getDesign(
            designItem.design.customer?.id ||
              profile?.userOrganizationInformations.organizationId ||
              0,
            designItem.design.id
          )
          .then(mapToDesign),
      enabled:
        (!!designItem.design.customer?.id ||
          !!profile?.userOrganizationInformations.organizationId) &&
        designItem.design.id > 0,
      staleTime: 1 * 30 * 1000,
    })),
  });

  // "Prices on their way" / "Preparing logo…" (flags productPending /
  // cloneInstant): a logo line's fresh detail is copied into the cached
  // LIST rows of the same logo (pending flags + product only), so the logo
  // list the user returns to has dropped the badge already - 0 calls; the
  // LIST entries are written, never the detail keys these queries observe,
  // so this can never re-trigger the sync. Both flags off = no-op.
  //
  // Keyed on WHEN each line's detail landed (id:dataUpdatedAt), not on
  // logoQueries (a fresh array every render of the cart): the cache walk
  // runs once per landed GetLogo, not once per keystroke in a quantity
  // field. The queries themselves are read through a ref at that moment.
  const logoQueriesRef = useRef(logoQueries);
  logoQueriesRef.current = logoQueries;
  const landedSignature = logoQueries
    .map((query) =>
      query.isSuccess && query.data
        ? `${(query.data as BaseLogo).id}:${query.dataUpdatedAt}`
        : ""
    )
    .join("|");
  useEffect(() => {
    if (landedSignature.replace(/\|/g, "") === "") return;
    logoQueriesRef.current.forEach((query) => {
      if (query.isSuccess && query.data) {
        patchLogoRowsFromDetail(queryClient, query.data as BaseLogo);
      }
    });
  }, [landedSignature, queryClient]);

  // Check if any queries are loading
  const isRefreshing =
    logoQueries.some((query) => query.isLoading || query.isFetching) ||
    designQueries.some((query) => query.isLoading || query.isFetching);

  // Function to check if a logo has stale data
  const isLogoStale = useCallback(
    (cartLogo: CartItemLogo, freshLogo: BaseLogo): boolean => {
      // "Prices on their way" (flag productPendingEnabled): a pending state
      // that changed is a reason to sync on its own - the worker may land
      // the very same prices, and the line must still drop its badge/lock.
      // Always false with the flag off.
      if (cartLogoPendingChanged(cartLogo.baseLogo, freshLogo)) return true;

      // Compare prices directly using product.prices (row count, then the
      // fields row by row - the rule lives in priceFieldsDiffer, unchanged).
      return priceFieldsDiffer(
        cartLogo.baseLogo.product?.prices,
        freshLogo.product?.prices
      );
    },
    []
  );

  // Function to check if a design has stale data
  const isDesignStale = useCallback(
    (cartDesign: CartItemDesign, freshDesign: Design): boolean => {
      // "Prices on their way" (flag productPendingEnabled): see isLogoStale -
      // covers the design's flag, its placed logos and the instance rows'
      // baseLogo copies. Always false with the flag off.
      if (cartDesignPendingChanged(cartDesign, freshDesign)) return true;

      // Cart price check (flag cartPriceCheckEnabled): the design's total
      // prices moved - the line must take the fresh total. Always false
      // with the flag off.
      if (designTotalPricesChanged(cartDesign, freshDesign)) return true;

      // Check if design logos have changed in storage or prices
      // This is simplified; ideally we check everything, but focusing on inStorage
      const cartLogos = cartDesign.design.designLogos || [];
      // Compare against the fresh logos minus any newly-deleted ones, since the
      // cart intentionally filters out deleted logos at entry.
      const freshLogos = (freshDesign.designLogos || []).filter(
        (dl) => !dl.logo.isDeleted
      );

      if (cartLogos.length !== freshLogos.length) return true;

      // Check if any logo has changed storage
      return cartLogos.some((cartLogo) => {
        const freshLogo = freshLogos.find((f) => f.logoId === cartLogo.logoId);
        if (!freshLogo) return true;

        const cartStorage = cartLogo.logo.inStorage ?? 0;
        const freshStorage = freshLogo.logo.inStorage ?? 0;

        if (cartStorage !== freshStorage) return true;

        // Also check prices of logos if needed, similar to isLogoStale
        // But inStorage is the main concern here
        return false;
      });
    },
    []
  );

  // Cart price check (flag cartPriceCheckEnabled): EVERY landed detail is
  // handed to the check - stale or not - so a logo the API reported
  // "updated" has its mark consumed by the very re-read the check asked
  // for, and the "Price updated" note is decided against the rows the user
  // saw at add time (landCartPriceCheck). Idempotent after the first
  // landing (the mark is gone), so the per-render re-run costs a few map
  // reads. Skipped entirely with the flag off.
  const landCartPriceChecks = useCallback(() => {
    if (!enviroment.cartPriceCheckEnabled) return;
    logoQueries.forEach((query, index) => {
      const cartItem = logoItems[index];
      const freshLogo = query.data;
      if (!query.isSuccess || !freshLogo) return;
      landCartPriceCheck(
        cartItem.logoId,
        freshLogo.product?.prices,
        priceFieldsDiffer(
          cartItem.baseLogo.product?.prices,
          freshLogo.product?.prices
        )
      );
    });
    designQueries.forEach((query, index) => {
      const cartItem = designItems[index];
      const freshDesign = query.data;
      if (!query.isSuccess || !freshDesign) return;
      // The fresh design carries each placed logo's own price rows; the
      // design total's diff is the fallback for a logo it does not list.
      const totalDiffers = designTotalPricesChanged(cartItem, freshDesign);
      collectCartLogoIds([cartItem]).forEach((logoId) => {
        const placed = (freshDesign.designLogos ?? []).find(
          (dl) => dl.logoId === logoId
        );
        landCartPriceCheck(logoId, placed?.logo?.product?.prices, totalDiffers);
      });
    });
  }, [logoQueries, designQueries, logoItems, designItems]);

  // Function to update stale cart items
  const syncStaleItems = useCallback(async () => {
    const updates: Promise<CartItem>[] = [];

    landCartPriceChecks();

    logoQueries.forEach((query, index) => {
      const cartItem = logoItems[index];
      const freshLogo = query.data;

      if (query.isSuccess && freshLogo && isLogoStale(cartItem, freshLogo)) {
        // Create updated cart item with fresh logo data
        const updatedCartItem: CartItemLogo = {
          ...cartItem,
          baseLogo: freshLogo,
        };

        updates.push(updateItem(updatedCartItem));
      }
    });

    designQueries.forEach((query, index) => {
      const cartItem = designItems[index];
      const freshDesign = query.data;

      if (
        query.isSuccess &&
        freshDesign &&
        isDesignStale(cartItem, freshDesign)
      ) {
        // Re-apply the same filter the cart uses at entry: deleted logos must
        // not appear in design.designLogos, but are tracked separately so the
        // UI can show a warning and the order omits them.
        const freshDeletedDesignLogos = (freshDesign.designLogos ?? []).filter(
          (dl) => dl.logo.isDeleted
        );
        const filteredFreshDesign: Design = {
          ...freshDesign,
          designLogos: (freshDesign.designLogos ?? []).filter(
            (dl) => !dl.logo.isDeleted
          ),
        };

        const updatedCartItem: CartItemDesign = {
          ...cartItem,
          design: filteredFreshDesign,
          excludedDeletedDesignLogos:
            freshDeletedDesignLogos.length > 0
              ? freshDeletedDesignLogos
              : undefined,
          // Update instances' logos' baseLogos with fresh data, but skip any
          // instance logo that has become deleted.
          instances: cartItem.instances.map((inst) => ({
            ...inst,
            logos: inst.logos.map((l) => {
              const freshLogoWrapper = filteredFreshDesign.designLogos.find(
                (dl) => dl.logoId === l.logoId
              );
              if (freshLogoWrapper) {
                return { ...l, baseLogo: freshLogoWrapper.logo };
              }
              return l;
            }),
          })),
        };

        updates.push(updateItem(updatedCartItem));
      }
    });

    if (updates.length > 0) {
      try {
        await Promise.all(updates);
      } catch (error) {
        console.error("Failed to update stale cart items:", error);
      }
    }
  }, [
    logoQueries,
    designQueries,
    logoItems,
    designItems,
    isLogoStale,
    isDesignStale,
    updateItem,
    landCartPriceChecks,
  ]);

  // Check if there are any stale items
  const hasStaleItems =
    logoQueries.some((query, index) => {
      const cartItem = logoItems[index];
      const freshLogo = query.data;
      return query.isSuccess && freshLogo && isLogoStale(cartItem, freshLogo);
    }) ||
    designQueries.some((query, index) => {
      const cartItem = designItems[index];
      const freshDesign = query.data;
      return (
        query.isSuccess && freshDesign && isDesignStale(cartItem, freshDesign)
      );
    });

  // Auto-sync when fresh data is available
  useEffect(() => {
    if (
      !isRefreshing &&
      (logoQueries.length > 0 || designQueries.length > 0) &&
      logoQueries.every((q) => q.isSuccess || q.isError) &&
      designQueries.every((q) => q.isSuccess || q.isError)
    ) {
      syncStaleItems();
    }
  }, [isRefreshing, logoQueries, designQueries, syncStaleItems]);

  // Manual refresh function
  const refreshCart = useCallback(() => {
    logoQueries.forEach((query) => {
      if (query.refetch) {
        query.refetch();
      }
    });
    designQueries.forEach((query) => {
      if (query.refetch) {
        query.refetch();
      }
    });
  }, [logoQueries, designQueries]);

  return {
    isRefreshing,
    hasStaleItems,
    refreshCart,
  };
}
